import React, { useState } from 'react';
import { geocode, reverseGeocode } from '../../utils/geocoding';
import { hasNonEmptyValue, hasValidCoordinatePair } from '../../utils';
import type { ListingDedication, ListingStatus, Permission } from '../../types';
import type { RoutePlannerPoint } from './listingEditor';

export interface ListingFormState {
  title: string;
  description: string;
  listing_status: ListingStatus;
  Dedicated: ListingDedication;
  order_value: string | number;
  pilots_quantity: string;
  permissions: Permission[];
  position_start: {
    city: string;
    address: string;
    lat: string;
    lng: string;
  };
  end_position: {
    city: string;
    address: string;
    lat: string;
    lng: string;
  };
  time_frame: {
    startDate: string;
    endDate: string;
  };
  route_points: RoutePlannerPoint[];
  route_geojson: any;
}

export type ListingLocationKey = 'position_start' | 'end_position';

export function createInitialListingFormState(initialData?: Partial<ListingFormState>): ListingFormState {
  return {
    title: initialData?.title || '',
    description: initialData?.description || '',
    listing_status: (initialData?.listing_status || 'OPEN') as ListingStatus,
    Dedicated: (initialData?.Dedicated || 'NONE') as ListingDedication,
    order_value: initialData?.order_value || '',
    pilots_quantity: initialData?.pilots_quantity?.toString() || '1',
    permissions: (initialData?.permissions || []) as Permission[],
    position_start: {
      city: initialData?.position_start?.city || '',
      address: initialData?.position_start?.address || '',
      lat: initialData?.position_start?.lat?.toString() || '',
      lng: initialData?.position_start?.lng?.toString() || '',
    },
    end_position: {
      city: initialData?.end_position?.city || '',
      address: initialData?.end_position?.address || '',
      lat: initialData?.end_position?.lat?.toString() || '',
      lng: initialData?.end_position?.lng?.toString() || '',
    },
    time_frame: {
      startDate: initialData?.time_frame?.startDate ? new Date(initialData.time_frame.startDate).toISOString().split('T')[0] : '',
      endDate: initialData?.time_frame?.endDate ? new Date(initialData.time_frame.endDate).toISOString().split('T')[0] : '',
    },
    route_points: initialData?.route_points || [],
    route_geojson: initialData?.route_geojson || null,
  };
}

export function validateListingFormData(formData: {
  title: string;
  description: string;
  order_value: string | number;
  pilots_quantity: string;
  permissions: Permission[];
  position_start: { lat: string; lng: string };
  end_position: { lat: string; lng: string };
  time_frame: { startDate: string; endDate: string };
}, options?: { enforceFutureDates?: boolean }) {
  if (!hasNonEmptyValue(formData.title) || !hasNonEmptyValue(formData.description)) {
    return 'Tytuł i opis ogłoszenia są wymagane.';
  }

  const pilotsQuantity = Number.parseInt(formData.pilots_quantity, 10);
  if (!Number.isInteger(pilotsQuantity) || pilotsQuantity < 1) {
    return 'Liczba pilotów musi być liczbą całkowitą większą od zera.';
  }

  if (hasNonEmptyValue(String(formData.order_value ?? ''))) {
    const orderValue = Number(formData.order_value);
    if (!Number.isFinite(orderValue) || orderValue <= 0) {
      return 'Wartość zlecenia musi być liczbą większą od zera.';
    }
  }

  if (!hasNonEmptyValue(formData.position_start.lat) || !hasNonEmptyValue(formData.position_start.lng)) {
    return 'Współrzędne dla lokalizacji początkowej są wymagane.';
  }

  if (!hasValidCoordinatePair(formData.position_start.lat, formData.position_start.lng)) {
    return 'Podaj poprawne współrzędne dla lokalizacji początkowej.';
  }

  if (!hasNonEmptyValue(formData.end_position.lat) || !hasNonEmptyValue(formData.end_position.lng)) {
    return 'Współrzędne dla lokalizacji końcowej są wymagane.';
  }

  if (!hasValidCoordinatePair(formData.end_position.lat, formData.end_position.lng)) {
    return 'Podaj poprawne współrzędne dla lokalizacji końcowej.';
  }

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  if (formData.time_frame.startDate) {
    const startDate = new Date(formData.time_frame.startDate);
    if (options?.enforceFutureDates !== false && startDate < now) {
      return 'Data rozpoczęcia nie może być z przeszłości.';
    }
  } else {
    return 'Data rozpoczęcia jest wymagana.';
  }

  if (formData.time_frame.endDate) {
    const startDate = new Date(formData.time_frame.startDate);
    const endDate = new Date(formData.time_frame.endDate);
    if (options?.enforceFutureDates !== false && endDate < now) {
      return 'Data zakończenia nie może być z przeszłości.';
    }
    if (endDate < startDate) {
      return 'Data zakończenia nie może być wcześniejsza niż data rozpoczęcia.';
    }
  } else {
    return 'Data zakończenia jest wymagana.';
  }

  return null;
}

function useSynchronizedListingLocation(
  formData: ListingFormState,
  setFormData: React.Dispatch<React.SetStateAction<ListingFormState>>,
  type: ListingLocationKey,
  isInternalUpdate: React.MutableRefObject<boolean>
) {
  const location = formData[type];

  React.useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const timer = window.setTimeout(async () => {
      if (!location.city) return;

      const result = await geocode(location.city);
      if (result) {
        isInternalUpdate.current = true;
        setFormData((prev) => ({
          ...prev,
          [type]: {
            ...prev[type],
            lat: result.lat.toString(),
            lng: result.lng.toString(),
          },
        }));
      }
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [isInternalUpdate, location.city, setFormData, type]);

  React.useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const timer = window.setTimeout(async () => {
      const lat = parseFloat(location.lat);
      const lng = parseFloat(location.lng);

      if (!Number.isNaN(lat) && !Number.isNaN(lng) && lat !== 0 && lng !== 0) {
        const result = await reverseGeocode(lat, lng);
        if (result?.city) {
          isInternalUpdate.current = true;
          setFormData((prev) => ({
            ...prev,
            [type]: {
              ...prev[type],
              city: result.city || prev[type].city,
            },
          }));
        }
      }
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [isInternalUpdate, location.lat, location.lng, setFormData, type]);
}

export function useListingForm(initialData?: Partial<ListingFormState>) {
  const [formData, setFormData] = useState<ListingFormState>(() => createInitialListingFormState(initialData));
  const isInternalUpdate = React.useRef(false);

  useSynchronizedListingLocation(formData, setFormData, 'position_start', isInternalUpdate);
  useSynchronizedListingLocation(formData, setFormData, 'end_position', isInternalUpdate);

  const handleLocationSelect = async (type: ListingLocationKey, lat: number, lng: number) => {
    isInternalUpdate.current = true;
    setFormData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        lat: lat.toString(),
        lng: lng.toString(),
      },
    }));

    const result = await reverseGeocode(lat, lng);
    if (result?.city) {
      isInternalUpdate.current = true;
      setFormData((prev) => ({
        ...prev,
        [type]: {
          ...prev[type],
          city: result.city || prev[type].city,
        },
      }));
    }
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDescriptionChange = (value?: string) => {
    setFormData((prev) => ({ ...prev, description: value || '' }));
  };

  const handleSelectChange = (e: any) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleLocationChange = (type: ListingLocationKey, field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [type]: { ...prev[type], [field]: value },
    }));
  };

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setFormData((prev) => ({
      ...prev,
      time_frame: { ...prev.time_frame, [field]: value },
    }));
  };

  const handlePermissionChange = (permission: Permission) => {
    setFormData((prev) => {
      const current = [...prev.permissions];
      return current.includes(permission)
        ? { ...prev, permissions: current.filter((item) => item !== permission) }
        : { ...prev, permissions: [...current, permission] };
    });
  };

  return {
    formData,
    setFormData,
    handleFieldChange,
    handleDescriptionChange,
    handleSelectChange,
    handleLocationChange,
    handleDateChange,
    handlePermissionChange,
    handleLocationSelect,
  };
}
